import { useContext, useEffect } from 'react'
import {
  PageHeaderActionsContext,
  type PageHeaderConfig
} from '@/providers/PageHeaderProvider'

/**
 * Hook to set page header content (breadcrumb, title, actions) in the main AppLayout header.
 * Automatically clears the header when component unmounts.
 *
 * IMPORTANT: The config object should be memoized in the calling component using useMemo
 * to prevent unnecessary re-renders and ensure proper updates when navigating.
 *
 * With the split context design, components using this hook will NOT re-render when the
 * header value changes - they only trigger updates. This prevents infinite render loops.
 *
 * @example
 * ```tsx
 * const pageHeaderConfig = useMemo(() => ({
 *   breadcrumb: <Breadcrumb items={[...]} />,
 *   title: <><h1>{hospital.name}</h1><Tag>CMS: {hospital.cmsid}</Tag></>,
 *   extra: <Button>Edit</Button>
 * }), [hospital.name, hospital.cmsid])
 *
 * usePageHeader(pageHeaderConfig)
 * ```
 */
export const usePageHeader = (config: PageHeaderConfig) => {
  const actions = useContext(PageHeaderActionsContext)

  if (!actions) {
    throw new Error('usePageHeader must be used within a PageHeaderProvider')
  }

  const { setPageHeader, clearPageHeader } = actions

  useEffect(() => {
    // Set the header with the memoized config
    setPageHeader(config)

    // Cleanup: clear header when component unmounts
    return () => {
      clearPageHeader()
    }
  }, [config, setPageHeader, clearPageHeader]) // All dependencies now safe - actions are stable

  return actions
}
